import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoFilter, IoClose, IoCheckmarkCircle, IoAlertCircle, IoCall, IoStar, IoStarOutline, IoSparkles, IoTicket, IoPerson, IoLocation, IoCart, IoReceipt, IoTime, IoRefresh, IoWarning } from "react-icons/io5";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { get1StarReviews, getTicketByOrderId, resolveRating, unresolveRating } from "../../services/ClientService";
import { toast } from "react-toastify";

const RatingsAndReviews = () => {
    const [selectedReview, setSelectedReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshingModal, setRefreshingModal] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [filters, setFilters] = useState({ isActive: [true, false] });
    const [startDate, endDate] = dateRange;
    const [showFilterPopup, setShowFilterPopup] = useState(false);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const filterPayload = {};
            
            if (startDate && endDate) {
                filterPayload.from = startDate.toISOString();
                filterPayload.to = endDate.toISOString();
            }

            const response = await get1StarReviews(filterPayload);
            
            if (response.success) {
                setReviews(response.data || []);
            } else {
                toast.error(response.message || "Failed to fetch reviews");
            }
        } catch (error) {
            console.error("Error fetching reviews:", error);
            toast.error("Error loading ratings and reviews");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            fetchReviews();
        }
    }, [startDate, endDate]);

    const handleClearDateFilter = () => {
        setDateRange([null, null]);
        fetchReviews();
    }

    const handleReviewClick = (review) => {
        setSelectedReview(review);
    };

    const closeDetails = () => {
        setSelectedReview(null);
    };

    const refreshSelectedTicket = async () => {
        if (!selectedReview?.orderId) return;
        
        try {
            setRefreshingModal(true);
            const response = await getTicketByOrderId(selectedReview.orderId);
            if (response.success && response.data) {
                setSelectedReview(response.data);
                toast.success("Ticket data refreshed");
            } else {
                toast.error(response.message || "Failed to refresh ticket");
            }
        } catch (error) {
            console.error("Error refreshing ticket:", error);
            toast.error("Error refreshing ticket data");
        } finally {
            setRefreshingModal(false);
        }
    };

    const handleMarkAsResolved = async () => {
        if (!selectedReview) return;
        
        try {
            const response = await resolveRating(null, selectedReview.orderId);
            if (response.success) {
                toast.success(`${response.data?.resolvedCount || 'All'} rating(s) marked as resolved`);
                setSelectedReview({ ...selectedReview, isResolved: true });
                fetchReviews();
            } else {
                toast.error(response.message || "Failed to resolve ratings");
            }
        } catch (error) {
            console.error("Error resolving ratings:", error);
            toast.error("Error resolving ratings");
        }
    };

    const handleMarkAsUnresolved = async () => {
        if (!selectedReview) return;
        
        try {
            const response = await unresolveRating(null, selectedReview.orderId);
            if (response.success) {
                toast.success(`${response.data?.unresolvedCount || 'All'} rating(s) reopened`);
                setSelectedReview({ ...selectedReview, isResolved: false });
                fetchReviews();
            } else {
                toast.error(response.message || "Failed to reopen ticket");
            }
        } catch (error) {
            console.error("Error reopening ticket:", error);
            toast.error("Error reopening ticket");
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const openCount = reviews.filter(r => !r.isResolved).length;
    const resolvedCount = reviews.filter(r => r.isResolved).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30">
            {/* Header Section */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/80 backdrop-blur-md border-b border-gray-200/80 sticky top-0 z-20 shadow-sm"
            >
                <div className="px-6 py-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl blur-lg opacity-30" />
                            <div className="relative bg-gradient-to-r from-rose-500 to-orange-500 p-3.5 rounded-2xl shadow-lg">
                                <IoTicket className="text-2xl text-white" />
                            </div>
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                                Support Tickets
                            </h1>
                            <p className="text-gray-500 text-sm flex items-center gap-1.5 mt-0.5">
                                <IoStar className="text-rose-500" />
                                1-Star Reviews Dashboard
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Stats Pills */}
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="hidden md:flex items-center gap-3 bg-white rounded-2xl px-5 py-2.5 border border-gray-200 shadow-sm"
                        >
                            <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                                <span className="text-gray-700 text-sm font-semibold">{openCount} Open</span>
                            </div>
                            <div className="w-px h-5 bg-gray-200" />
                            <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                <span className="text-gray-700 text-sm font-semibold">{resolvedCount} Resolved</span>
                            </div>
                        </motion.div>

                        {/* Filter Button */}
                        <motion.button
                            onClick={() => setShowFilterPopup(!showFilterPopup)}
                            className="relative flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 transition-all duration-300 shadow-sm hover:shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <IoFilter className="text-lg" />
                            <span className="font-semibold text-sm">Filters</span>
                            {(startDate && endDate) && (
                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-white" />
                            )}
                        </motion.button>
                    </div>

                    {/* Filter Popup */}
                    <AnimatePresence>
                        {showFilterPopup && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                className="absolute top-full right-6 mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50"
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-gray-800 font-semibold">Select Date Range</h3>
                                        <button 
                                            onClick={() => setShowFilterPopup(false)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <IoClose className="text-xl" />
                                        </button>
                                    </div>
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => {
                                            setDateRange(update);
                                            if (update[0] && update[1]) {
                                                setShowFilterPopup(false);
                                            }
                                        }}
                                        isClearable={true}
                                        inline
                                        shouldCloseOnSelect={false}
                                    />
                                    <motion.button
                                        onClick={() => {
                                            handleClearDateFilter();
                                            setShowFilterPopup(false);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="mt-3 w-full px-4 py-2.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300"
                                    >
                                        Clear Filter
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* Reviews Section */}
            {loading ? (
                <div className="flex flex-col justify-center items-center h-[calc(100vh-120px)]">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="relative"
                    >
                        <div className="w-16 h-16 border-4 border-gray-200 border-t-rose-500 rounded-full animate-spin" />
                    </motion.div>
                    <p className="mt-6 text-gray-500 font-medium">Loading tickets...</p>
                </div>
            ) : reviews.length === 0 ? (
                <div className="flex flex-col justify-center items-center h-[calc(100vh-120px)]">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <div className="relative inline-block mb-6">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-3xl blur-2xl" />
                            <div className="relative bg-gradient-to-br from-emerald-50 to-teal-50 p-10 rounded-3xl border border-emerald-200/50 shadow-lg">
                                <IoSparkles className="text-6xl text-emerald-500 mx-auto" />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">All Clear!</h2>
                        <p className="text-gray-500 max-w-md">No 1-star reviews to address. Your customers are happy!</p>
                    </motion.div>
                </div>
            ) : (
                <div className="overflow-y-auto h-full max-h-[calc(100vh-100px)] px-6 py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
                        {reviews.map((review, index) => (
                            <motion.div
                                key={review._id || review.orderId}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                onClick={() => handleReviewClick(review)}
                                className={`group relative cursor-pointer overflow-hidden rounded-2xl bg-white border-2 transition-all duration-300 hover:shadow-xl ${
                                    review.isResolved 
                                        ? 'border-gray-100 hover:border-emerald-200' 
                                        : 'border-rose-100 hover:border-rose-300 shadow-lg shadow-rose-100/50'
                                }`}
                                whileHover={{ y: -4 }}
                            >
                                {/* Status Indicator Bar */}
                                <div className={`absolute top-0 left-0 right-0 h-1.5 ${
                                    review.isResolved 
                                        ? 'bg-gradient-to-r from-emerald-400 to-teal-400' 
                                        : 'bg-gradient-to-r from-rose-500 to-orange-500'
                                }`} />

                                <div className="p-5 pt-6">
                                    {/* Header Row */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            {/* Star Rating Visual */}
                                            <div className="flex items-center gap-1.5 bg-rose-50 px-3 py-1.5 rounded-xl border border-rose-100">
                                                <IoStar className="text-rose-500 text-sm" />
                                                <span className="text-rose-600 font-bold text-sm">1</span>
                                            </div>
                                            {/* Product Count Badge */}
                                            <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                                                <IoCart className="text-gray-500 text-xs" />
                                                <span className="text-gray-700 font-semibold text-xs">
                                                    {review.ratedProductsCount || review.ratedProducts?.length || 1}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Status Badge */}
                                        <motion.div 
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold ${
                                                review.isResolved 
                                                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                                                    : 'bg-rose-50 text-rose-600 border border-rose-200'
                                            }`}
                                        >
                                            {review.isResolved ? (
                                                <><IoCheckmarkCircle className="text-sm" /> Resolved</>
                                            ) : (
                                                <><IoAlertCircle className="text-sm animate-pulse" /> Open</>
                                            )}
                                        </motion.div>
                                    </div>

                                    {/* Customer Info */}
                                    <div className="mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg font-bold shadow-sm ${
                                                review.isResolved 
                                                    ? 'bg-gradient-to-br from-gray-100 to-gray-50 text-gray-500' 
                                                    : 'bg-gradient-to-br from-rose-100 to-orange-50 text-rose-600'
                                            }`}>
                                                {(review.customer?.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-gray-900 font-semibold truncate">{review.customer?.name || "Unknown"}</p>
                                                <p className="text-gray-500 text-sm truncate">{review.customer?.mobile || review.customer?.email || ""}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-4" />

                                    {/* Product Preview */}
                                    <div className="mb-3">
                                        {review.ratedProducts && review.ratedProducts.length > 0 ? (
                                            <p className="text-gray-800 font-medium text-sm line-clamp-1">
                                                {review.ratedProducts[0]?.product?.productName || "Unknown Product"}
                                                {review.ratedProducts.length > 1 && (
                                                    <span className="text-gray-400"> +{review.ratedProducts.length - 1} more</span>
                                                )}
                                            </p>
                                        ) : (
                                            <p className="text-gray-800 font-medium text-sm line-clamp-1">
                                                {review.product?.productName || "Unknown Product"}
                                            </p>
                                        )}
                                        <p className="text-gray-400 text-xs mt-1 flex items-center gap-1">
                                            <IoReceipt className="text-xs" />
                                            Order #{review.order?.saleId || review.order?._id?.slice(-6) || "N/A"}
                                        </p>
                                    </div>

                                    {/* Review Text */}
                                    {review.review && (
                                        <div className="bg-gray-50 rounded-xl p-3 mb-3 border border-gray-100">
                                            <p className="text-gray-600 text-sm line-clamp-2 italic">"{review.review}"</p>
                                        </div>
                                    )}

                                    {/* Footer */}
                                    <div className="flex items-center justify-between pt-2">
                                        <span className="text-gray-400 text-xs flex items-center gap-1">
                                            <IoTime className="text-xs" />
                                            {formatDate(review.ticketCreatedAt || review.createdAt)}
                                        </span>
                                        <span className="text-gray-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                            Click to view →
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Detailed Modal */}
                    <AnimatePresence>
                        {selectedReview && (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center px-4 md:px-8 z-50"
                            >
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                    className="bg-white w-full md:w-4/5 lg:w-3/4 xl:w-2/3 2xl:w-1/2 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] border border-gray-100"
                                >
                                    {/* Modal Header */}
                                    <div className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-orange-500 to-rose-500">
                                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cmVjdCB4PSIwIiB5PSIwIiB3aWR0aD0iMiIgaGVpZ2h0PSI0MCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCBmaWxsPSJ1cmwoI2EpIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIi8+PC9zdmc+')] opacity-30" />
                                        
                                        <div className="relative px-6 py-6 flex justify-between items-start">
                                            <div className="flex items-center gap-4">
                                                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl">
                                                    <IoTicket className="text-2xl text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-2xl font-bold text-white">Ticket Details</h2>
                                                    <p className="text-white/80 text-sm mt-0.5 flex items-center gap-2">
                                                        <IoReceipt className="text-xs" />
                                                        Order #{selectedReview.order?.saleId || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <motion.button
                                                    onClick={refreshSelectedTicket}
                                                    disabled={refreshingModal}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    className="text-white/80 hover:text-white bg-white/20 hover:bg-white/30 p-2.5 rounded-xl transition-colors disabled:opacity-50"
                                                    title="Refresh ticket data"
                                                >
                                                    <IoRefresh className={`text-xl ${refreshingModal ? 'animate-spin' : ''}`} />
                                                </motion.button>
                                                <motion.button
                                                    onClick={closeDetails}
                                                    whileHover={{ scale: 1.1, rotate: 90 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="text-white/80 hover:text-white bg-white/20 hover:bg-white/30 p-2.5 rounded-xl transition-colors"
                                                >
                                                    <IoClose className="text-xl" />
                                                </motion.button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Scrollable Content */}
                                    <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 space-y-5 bg-gray-50/50">
                                        
                                        {/* Status Action Card */}
                                        <motion.div 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`relative overflow-hidden rounded-2xl border-2 ${
                                                selectedReview.isResolved 
                                                    ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200' 
                                                    : 'bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200'
                                            }`}
                                        >
                                            <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                                                        selectedReview.isResolved 
                                                            ? 'bg-gradient-to-br from-emerald-500 to-teal-500' 
                                                            : 'bg-gradient-to-br from-rose-500 to-orange-500'
                                                    }`}>
                                                        {selectedReview.isResolved 
                                                            ? <IoCheckmarkCircle className="text-3xl text-white" /> 
                                                            : <IoAlertCircle className="text-3xl text-white animate-pulse" />
                                                        }
                                                    </div>
                                                    <div>
                                                        <p className={`font-bold text-lg ${
                                                            selectedReview.isResolved ? 'text-emerald-700' : 'text-rose-700'
                                                        }`}>
                                                            {selectedReview.isResolved ? 'Issue Resolved' : 'Pending Resolution'}
                                                        </p>
                                                        <p className="text-gray-500 text-sm">
                                                            Last updated: {selectedReview.resolvedAt ? formatDate(selectedReview.resolvedAt) : formatDate(selectedReview.createdAt)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 w-full sm:w-auto">
                                                    {!selectedReview.isResolved ? (
                                                        <motion.button
                                                            onClick={handleMarkAsResolved}
                                                            whileHover={{ scale: 1.02 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-emerald-200 transition-all font-semibold"
                                                        >
                                                            <IoCheckmarkCircle className="text-lg" />
                                                            Mark Resolved
                                                        </motion.button>
                                                    ) : (
                                                        <motion.button
                                                            onClick={handleMarkAsUnresolved}
                                                            whileHover={{ scale: 1.02 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-amber-200 transition-all font-semibold"
                                                        >
                                                            <IoRefresh className="text-lg" />
                                                            Reopen Ticket
                                                        </motion.button>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>

                                        {/* Two Column Layout */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                            {/* Customer Card */}
                                            <motion.div 
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 }}
                                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
                                                            <IoPerson className="text-blue-500 text-lg" />
                                                        </div>
                                                        <h3 className="font-bold text-gray-800">Customer</h3>
                                                    </div>
                                                    {selectedReview.customer?.mobile && selectedReview.customer.mobile !== 'N/A' && (
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => window.open(`tel:${selectedReview.customer.mobile}`)}
                                                            className="flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl text-sm font-semibold border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                                        >
                                                            <IoCall />
                                                            Call
                                                        </motion.button>
                                                    )}
                                                </div>
                                                <div className="space-y-3">
                                                    <div className="flex items-start gap-3">
                                                        <span className="text-gray-400 text-sm w-16 flex-shrink-0">Name</span>
                                                        <span className="text-gray-800 font-medium text-sm">{selectedReview.customer?.name || "N/A"}</span>
                                                    </div>
                                                    <div className="flex items-start gap-3">
                                                        <span className="text-gray-400 text-sm w-16 flex-shrink-0">Email</span>
                                                        <span className="text-gray-600 text-sm break-all">{selectedReview.customer?.email || "N/A"}</span>
                                                    </div>
                                                    <div className="flex items-start gap-3">
                                                        <span className="text-gray-400 text-sm w-16 flex-shrink-0">Phone</span>
                                                        <span className="text-gray-600 text-sm">{selectedReview.customer?.mobile || "N/A"}</span>
                                                    </div>
                                                </div>
                                            </motion.div>

                                            {/* Delivery Card */}
                                            {selectedReview.delivery && (
                                                <motion.div 
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.15 }}
                                                    className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                                                >
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center border border-violet-100">
                                                            <IoLocation className="text-violet-500 text-lg" />
                                                        </div>
                                                        <h3 className="font-bold text-gray-800">Delivery Address</h3>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <div className="flex items-start gap-3">
                                                            <span className="text-gray-400 text-sm w-16 flex-shrink-0">Name</span>
                                                            <span className="text-gray-800 font-medium text-sm">{selectedReview.delivery?.name || "N/A"}</span>
                                                        </div>
                                                        <div className="flex items-start gap-3">
                                                            <span className="text-gray-400 text-sm w-16 flex-shrink-0">Mobile</span>
                                                            <span className="text-gray-600 text-sm">{selectedReview.delivery?.phone || "N/A"}</span>
                                                        </div>
                                                        <div className="flex items-start gap-3">
                                                            <span className="text-gray-400 text-sm w-16 flex-shrink-0">Address</span>
                                                            <span className="text-gray-600 text-sm leading-relaxed">
                                                                {selectedReview.delivery?.house && selectedReview.delivery?.area 
                                                                    ? `${selectedReview.delivery.house}, ${selectedReview.delivery.area}, ${selectedReview.delivery.address}`
                                                                    : selectedReview.delivery?.address || "N/A"}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 pt-2">
                                                            {selectedReview.delivery?.pincode && (
                                                                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-xs font-medium">
                                                                    📍 {selectedReview.delivery.pincode}
                                                                </span>
                                                            )}
                                                            <span className="bg-violet-50 text-violet-600 px-3 py-1 rounded-lg text-xs font-semibold border border-violet-100">
                                                                {selectedReview.delivery?.type || 'Home'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </div>

                                        {/* Ticket Summary */}
                                        <motion.div 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                            className="bg-gradient-to-br from-rose-50 via-white to-orange-50 p-5 rounded-2xl border-2 border-rose-100"
                                        >
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center border border-rose-200">
                                                    <IoTicket className="text-rose-500 text-lg" />
                                                </div>
                                                <h3 className="font-bold text-gray-800">Ticket Summary</h3>
                                            </div>

                                            <div className="bg-white rounded-xl p-4 border border-rose-100 shadow-sm">
                                                <div className="flex flex-wrap items-center gap-3 mb-4">
                                                    {/* Star Visual */}
                                                    <div className="flex items-center gap-0.5">
                                                        {[1,2,3,4,5].map(star => (
                                                            star === 1 
                                                                ? <IoStar key={star} className="text-rose-500 text-xl" />
                                                                : <IoStarOutline key={star} className="text-gray-300 text-xl" />
                                                        ))}
                                                    </div>
                                                    <span className="bg-rose-100 text-rose-600 px-3 py-1 rounded-full text-sm font-bold border border-rose-200">
                                                        {selectedReview.ratedProductsCount || selectedReview.ratedProducts?.length || 1} Product{(selectedReview.ratedProductsCount || 1) > 1 ? 's' : ''} Rated
                                                    </span>
                                                    <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs font-medium">
                                                        {formatDate(selectedReview.ticketCreatedAt || selectedReview.createdAt)}
                                                    </span>
                                                </div>

                                                {selectedReview.review && (
                                                    <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-rose-400">
                                                        <p className="text-gray-500 text-xs mb-2 font-semibold uppercase tracking-wide">Customer Feedback</p>
                                                        <p className="text-gray-700 text-sm leading-relaxed italic">"{selectedReview.review}"</p>
                                                    </div>
                                                )}

                                                {selectedReview.hasUpdatedRatings && (
                                                    <div className="mt-3 flex items-center gap-2 text-emerald-600 text-xs font-semibold bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200">
                                                        <IoCheckmarkCircle />
                                                        Some ratings have been updated by customer
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>

                                        {/* Order Information */}
                                        <motion.div 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.25 }}
                                            className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                                        >
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100">
                                                    <IoCart className="text-emerald-500 text-lg" />
                                                </div>
                                                <h3 className="font-bold text-gray-800">Order Information</h3>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                                                    <p className="text-gray-400 text-xs mb-1">Order ID</p>
                                                    <p className="text-gray-800 font-mono font-semibold text-sm">{selectedReview.order?.saleId || selectedReview.order?._id || "N/A"}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                                                    <p className="text-gray-400 text-xs mb-1">Status</p>
                                                    <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-semibold border border-emerald-200">
                                                        {selectedReview.order?.status || "N/A"}
                                                    </span>
                                                </div>
                                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                                                    <p className="text-gray-400 text-xs mb-1">Order Date</p>
                                                    <p className="text-gray-800 font-medium text-sm">
                                                        {selectedReview.order?.orderPlacedOn ? formatDate(selectedReview.order.orderPlacedOn) : "N/A"}
                                                    </p>
                                                </div>
                                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                                                    <p className="text-gray-400 text-xs mb-1">Time</p>
                                                    <p className="text-gray-800 font-medium text-sm">
                                                        {selectedReview.order?.orderPlacedOn ? new Date(selectedReview.order.orderPlacedOn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : "N/A"}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>

                                        {/* 1-Star Rated Products */}
                                        {selectedReview.ratedProducts && selectedReview.ratedProducts.length > 0 && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.3 }}
                                                className="bg-gradient-to-br from-rose-50 via-white to-orange-50 p-5 rounded-2xl border-2 border-rose-200"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center border border-rose-200">
                                                            <IoWarning className="text-rose-500 text-lg" />
                                                        </div>
                                                        <h3 className="font-bold text-gray-800">1-Star Rated Products</h3>
                                                    </div>
                                                    <span className="bg-rose-500 text-white px-3 py-1.5 rounded-xl text-xs font-bold shadow-lg shadow-rose-200">
                                                        {selectedReview.ratedProducts.length} Item{selectedReview.ratedProducts.length > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                
                                                <div className="space-y-3">
                                                    {selectedReview.ratedProducts.map((ratedProduct, index) => {
                                                        const productImage = ratedProduct.product?.image;
                                                        const hasUpdatedRating = ratedProduct.updatedRating !== null;
                                                        
                                                        return (
                                                            <motion.div
                                                                key={ratedProduct.ratingId || index}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: 0.35 + (index * 0.05) }}
                                                                className="relative bg-white p-4 rounded-xl border border-gray-100 shadow-sm"
                                                            >
                                                                {/* Status Badge */}
                                                                <div className="absolute -top-2 -right-2 z-10">
                                                                    <div className={`px-3 py-1 rounded-xl shadow-lg flex items-center gap-1.5 text-xs font-bold text-white ${
                                                                        hasUpdatedRating 
                                                                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                                                                            : ratedProduct.isResolved 
                                                                                ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                                                                                : 'bg-gradient-to-r from-rose-500 to-orange-500'
                                                                    }`}>
                                                                        {hasUpdatedRating ? (
                                                                            <><IoStar className="text-yellow-300" /> {ratedProduct.updatedRating}/5</>
                                                                        ) : ratedProduct.isResolved ? (
                                                                            <><IoTime /> Awaiting</>
                                                                        ) : (
                                                                            <><IoStar className="text-yellow-300" /> 1 Star</>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="flex gap-4">
                                                                    {/* Product Image */}
                                                                    <div className="flex-shrink-0">
                                                                        {productImage ? (
                                                                            <img 
                                                                                src={productImage} 
                                                                                alt={ratedProduct.product?.productName || 'Product'}
                                                                                className="w-16 h-16 object-cover rounded-xl shadow-md border border-gray-100"
                                                                            />
                                                                        ) : (
                                                                            <div className="w-16 h-16 rounded-xl flex items-center justify-center text-xl bg-gray-100 border border-gray-200">
                                                                                📦
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Product Details */}
                                                                    <div className="flex-1 min-w-0">
                                                                        <h4 className="font-semibold text-gray-800 text-sm leading-tight mb-2 truncate">
                                                                            {ratedProduct.product?.productName || 'Unknown Product'}
                                                                        </h4>
                                                                        {ratedProduct.review && (
                                                                            <p className="text-gray-500 text-xs line-clamp-2 italic">
                                                                                "{ratedProduct.review}"
                                                                            </p>
                                                                        )}
                                                                        {hasUpdatedRating && ratedProduct.updatedReview && (
                                                                            <div className="mt-2 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg">
                                                                                <p className="text-emerald-700 text-xs">
                                                                                    <span className="font-semibold">Updated:</span> "{ratedProduct.updatedReview}"
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* All Products in Order */}
                                        <motion.div 
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.35 }}
                                            className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center border border-amber-200">
                                                        <IoCart className="text-amber-500 text-lg" />
                                                    </div>
                                                    <h3 className="font-bold text-gray-800">All Products in Order</h3>
                                                </div>
                                                <span className="bg-amber-50 text-amber-600 px-3 py-1.5 rounded-xl text-xs font-bold border border-amber-200">
                                                    {selectedReview.order?.items?.length || 0} Items
                                                </span>
                                            </div>
                                            
                                            {selectedReview.order?.items && selectedReview.order.items.length > 0 ? (
                                                <div className="space-y-3">
                                                    {selectedReview.order.items.map((item, index) => {
                                                        const ratedProducts = selectedReview.ratedProducts || [];
                                                        const itemCartId = item.cartId || item._id || '';
                                                        const itemProductId = (item.productInfo?._id || item.productId || '').toString();
                                                        
                                                        const isRatedProduct = ratedProducts.some(r => {
                                                            const ratingItemId = r.itemId || '';
                                                            const ratingProductId = (r.productId || '').toString();
                                                            return ratingItemId === itemCartId || 
                                                                   (ratingProductId && itemProductId && ratingProductId === itemProductId);
                                                        });
                                                        
                                                        const itemPrice = item.finalPricing?.priceWithGst || item.pricing?.discountPrice || 0;
                                                        const itemQuantity = item.quantity || 1;
                                                        const rawWeight = item.productInfo?.weight || (typeof item.selectedWeight === 'string' ? item.selectedWeight : (item.selectedWeight?.weight?.weightLabel || item.selectedWeight?.weight || item.selectedWeight?.weightLabel)) || item.productInfo?.productOptions?.weight?.weightLabel || item.productInfo?.selectedWeight?.weight?.weightLabel || null;
                                                        const itemWeight = (rawWeight && typeof rawWeight === 'object') ? (rawWeight.weightLabel || `${rawWeight.weightValue || ''} ${rawWeight.metricLabel || ''}`.trim() || 'N/A') : (rawWeight || 'N/A');
                                                        
                                                        let itemImage = null;
                                                        if (item.productInfo?.images) {
                                                            if (typeof item.productInfo.images === 'string') {
                                                                itemImage = item.productInfo.images;
                                                            } else if (item.productInfo.images.preview) {
                                                                itemImage = item.productInfo.images.preview;
                                                            } else if (Array.isArray(item.productInfo.images) && item.productInfo.images.length > 0) {
                                                                itemImage = item.productInfo.images[0]?.preview || item.productInfo.images[0]?.original || item.productInfo.images[0];
                                                            }
                                                        }
                                                        if (!itemImage && item.image) itemImage = item.image;
                                                        if (!itemImage && item.productImage) itemImage = item.productImage;
                                                        
                                                        return (
                                                            <motion.div
                                                                key={index}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: 0.4 + (index * 0.03) }}
                                                                className={`relative p-4 rounded-xl border transition-all ${
                                                                    isRatedProduct 
                                                                        ? 'bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200 shadow-md shadow-rose-100/50' 
                                                                        : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                                                                }`}
                                                            >
                                                                {/* 1-Star Badge */}
                                                                {isRatedProduct && (
                                                                    <div className="absolute -top-2 -right-2 z-10">
                                                                        <div className="bg-gradient-to-r from-rose-500 to-orange-500 text-white px-3 py-1 rounded-xl shadow-lg flex items-center gap-1 text-xs font-bold">
                                                                            <IoStar className="text-yellow-300" />
                                                                            1 Star
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="flex gap-4">
                                                                    {/* Product Image */}
                                                                    <div className="flex-shrink-0">
                                                                        {itemImage ? (
                                                                            <img 
                                                                                src={itemImage} 
                                                                                alt={item.productInfo?.productName || 'Product'}
                                                                                className={`w-16 h-16 object-cover rounded-xl shadow-md border ${
                                                                                    isRatedProduct ? 'border-rose-200' : 'border-gray-100'
                                                                                }`}
                                                                            />
                                                                        ) : (
                                                                            <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-xl ${
                                                                                isRatedProduct ? 'bg-rose-100 border border-rose-200' : 'bg-gray-100 border border-gray-200'
                                                                            }`}>
                                                                                📦
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Product Details */}
                                                                    <div className="flex-1 min-w-0">
                                                                        <h4 className={`font-semibold text-sm leading-tight mb-2 truncate ${
                                                                            isRatedProduct ? 'text-rose-800' : 'text-gray-800'
                                                                        }`}>
                                                                            {item.productInfo?.productName || 'Unknown Product'}
                                                                        </h4>
                                                                        
                                                                        {/* Stats Grid */}
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            <div className={`p-2 rounded-lg ${isRatedProduct ? 'bg-white/70' : 'bg-white'}`}>
                                                                                <p className="text-gray-400 text-xs">Qty</p>
                                                                                <p className={`font-bold text-sm ${isRatedProduct ? 'text-rose-700' : 'text-gray-800'}`}>
                                                                                    {itemQuantity}
                                                                                </p>
                                                                            </div>
                                                                            <div className={`p-2 rounded-lg ${isRatedProduct ? 'bg-white/70' : 'bg-white'}`}>
                                                                                <p className="text-gray-400 text-xs">Weight</p>
                                                                                <p className={`font-bold text-sm truncate ${isRatedProduct ? 'text-rose-700' : 'text-gray-800'}`}>
                                                                                    {itemWeight}
                                                                                </p>
                                                                            </div>
                                                                            <div className={`p-2 rounded-lg ${isRatedProduct ? 'bg-white/70' : 'bg-white'}`}>
                                                                                <p className="text-gray-400 text-xs">Price</p>
                                                                                <p className={`font-bold text-sm ${isRatedProduct ? 'text-rose-700' : 'text-emerald-600'}`}>
                                                                                    ₹{(itemPrice * itemQuantity).toFixed(0)}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8">
                                                    <p className="text-gray-400 text-sm">No product details available</p>
                                                </div>
                                            )}
                                        </motion.div>

                                        {/* Bill Details */}
                                        {selectedReview.order?.financial && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.4 }}
                                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                                            >
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center border border-indigo-100">
                                                        <IoReceipt className="text-indigo-500 text-lg" />
                                                    </div>
                                                    <h3 className="font-bold text-gray-800">Bill Details</h3>
                                                </div>
                                                <div className="space-y-3 text-sm">
                                                    {/* Price Header */}
                                                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                                                        <span className="text-gray-500">Price ({selectedReview.order.items?.length || 1} items)</span>
                                                        <span className="font-semibold text-gray-800">₹{(selectedReview.order.financial.totalOriginalPrice || 0).toFixed(2)}</span>
                                                    </div>

                                                    {/* GST Breakdown */}
                                                    {selectedReview.order.items && selectedReview.order.items.length > 0 && (
                                                        <div className="bg-indigo-50 p-3 rounded-xl space-y-2 border border-indigo-100">
                                                            <p className="text-xs font-semibold text-indigo-600 mb-2">GST Breakdown (Inclusive)</p>
                                                            {selectedReview.order.items.map((item, idx) => (
                                                                <div key={idx} className="flex justify-between text-xs">
                                                                    <span className="text-gray-600 truncate max-w-[200px]">{item.productInfo?.productName || 'Item'}</span>
                                                                    <span className="text-gray-700 font-medium">₹{(item.gstDetails?.totalGstAmount || 0).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Discount */}
                                                    {selectedReview.order.financial.totalDiscount > 0 && (
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-gray-500">Discount</span>
                                                            <span className="font-semibold text-emerald-600">-₹{(selectedReview.order.financial.totalDiscount || 0).toFixed(2)}</span>
                                                        </div>
                                                    )}

                                                    {/* Price After Discount */}
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-500">Price After Discount</span>
                                                        <span className="font-semibold text-gray-800">₹{(selectedReview.order.financial.subtotal || 0).toFixed(2)}</span>
                                                    </div>

                                                    {/* Delivery Charges */}
                                                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                                                        <span className="text-gray-500">Delivery Charges</span>
                                                        <span className="font-semibold text-blue-600">₹{(selectedReview.order.financial.deliveryCharges || 0).toFixed(2)}</span>
                                                    </div>

                                                    {/* Total */}
                                                    <div className="flex justify-between items-center pt-2 bg-gradient-to-r from-indigo-50 to-violet-50 p-4 rounded-xl border border-indigo-100">
                                                        <span className="font-bold text-gray-800 text-base">Total Amount</span>
                                                        <span className="font-bold text-indigo-600 text-xl">₹{(selectedReview.order.financial.finalAmount || 0)}</span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default RatingsAndReviews;
